/**
 * CloserCalendar — per-closer working hours editor.
 *
 * Setup:
 *  - Pick a closer from dropdown (admin sees all; closer sees their own).
 *  - Set timezone + slot length.
 *  - Per weekday: pill that toggles day on/off + start/end time inputs.
 *  - Add blackout dates (vacation, public holidays).
 *
 * Saved hours are auto-applied to the WhatsApp Flow date+time picker for that closer's leads.
 */
import React, { useEffect, useState } from "react";
import { Calendar as CalendarIcon, Save, Plus, X, Loader2, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL || "";
const DAYS = [
  { idx: "0", label: "Mon" },
  { idx: "1", label: "Tue" },
  { idx: "2", label: "Wed" },
  { idx: "3", label: "Thu" },
  { idx: "4", label: "Fri" },
  { idx: "5", label: "Sat" },
  { idx: "6", label: "Sun" },
];
const TIMEZONES = [
  { v: "America/New_York", l: "EST/EDT (US East)" },
  { v: "America/Los_Angeles", l: "PST/PDT (US West)" },
  { v: "Asia/Kolkata", l: "IST (India)" },
  { v: "Europe/London", l: "GMT/BST (UK)" },
  { v: "Asia/Dubai", l: "GST (UAE)" },
  { v: "Australia/Sydney", l: "AEST (Sydney)" },
];

export default function CloserCalendar({ currentUser }) {
  const [closers, setClosers] = useState([]);
  const [selected, setSelected] = useState(currentUser?.username || "admin");
  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const isAdmin = currentUser?.username === "admin";

  useEffect(() => {
    fetch(`${API}/api/demo/closers`).then((r) => r.json()).then((d) => setClosers(d.closers || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/demo/closer-calendar/${selected}`)
      .then((r) => r.json())
      .then((d) => { setCal(d); setLoading(false); });
  }, [selected]);

  const updateDay = (dayIdx, intervals) => {
    setCal((c) => ({ ...c, weeklyHours: { ...c.weeklyHours, [dayIdx]: intervals } }));
  };

  const toggleDay = (dayIdx) => {
    const cur = cal.weeklyHours[dayIdx] || [];
    if (cur.length) updateDay(dayIdx, []);
    else updateDay(dayIdx, [{ start: "08:00", end: "14:00" }]);
  };

  const setInterval = (dayIdx, i, key, val) => {
    const cur = [...(cal.weeklyHours[dayIdx] || [])];
    cur[i] = { ...cur[i], [key]: val };
    updateDay(dayIdx, cur);
  };

  const addBlackout = (date) => {
    if (!date) return;
    if ((cal.blackoutDates || []).includes(date)) return;
    setCal((c) => ({ ...c, blackoutDates: [...(c.blackoutDates || []), date].sort() }));
  };
  const removeBlackout = (date) => {
    setCal((c) => ({ ...c, blackoutDates: (c.blackoutDates || []).filter((d) => d !== date) }));
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await fetch(`${API}/api/demo/closer-calendar/${selected}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          timezone: cal.timezone,
          slotMinutes: cal.slotMinutes,
          weeklyHours: cal.weeklyHours,
          blackoutDates: cal.blackoutDates || [],
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cal) {
    return (
      <div className="settings-card settings-card-full">
        <div className="settings-card-header">
          <CalendarIcon className="w-5 h-5" />
          <h3>Closer Calendar</h3>
        </div>
        <div className="settings-card-body" style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-card settings-card-full" data-testid="settings-closer-calendar">
      <div className="settings-card-header">
        <CalendarIcon className="w-5 h-5" />
        <div>
          <h3>Closer Calendar</h3>
          <p>Working hours synced to the WhatsApp booking calendar — leads only see slots when this closer is free.</p>
        </div>
      </div>
      <div className="settings-card-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Closer + TZ + slot */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 140px", gap: 12 }}>
          <label style={{ fontSize: 12, color: "#475569" }}>Closer
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={!isAdmin}
              data-testid="cc-closer-select"
              style={{ display: "block", marginTop: 4, padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 6, width: "100%", fontSize: 13 }}
            >
              {closers.map((c) => <option key={c.username} value={c.username}>{c.name} ({c.username})</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: "#475569" }}>Timezone
            <select
              value={cal.timezone}
              onChange={(e) => setCal({ ...cal, timezone: e.target.value })}
              data-testid="cc-tz-select"
              style={{ display: "block", marginTop: 4, padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 6, width: "100%", fontSize: 13 }}
            >
              {TIMEZONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: "#475569" }}>Slot length
            <select
              value={cal.slotMinutes}
              onChange={(e) => setCal({ ...cal, slotMinutes: parseInt(e.target.value, 10) })}
              data-testid="cc-slot-select"
              style={{ display: "block", marginTop: 4, padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 6, width: "100%", fontSize: 13 }}
            >
              <option value="15">15 min</option>
              <option value="20">20 min</option>
              <option value="30">30 min</option>
              <option value="45">45 min</option>
              <option value="60">60 min</option>
            </select>
          </label>
        </div>

        {/* Weekly hours */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 8 }}>Weekly hours</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {DAYS.map((d) => {
              const intervals = cal.weeklyHours[d.idx] || [];
              const isOff = intervals.length === 0;
              return (
                <div key={d.idx} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }} data-testid={`cc-day-${d.idx}`}>
                  <button
                    onClick={() => toggleDay(d.idx)}
                    style={{
                      width: 60, padding: "6px 0", borderRadius: 16,
                      background: isOff ? "#f1f5f9" : "#dcfce7",
                      color: isOff ? "#94a3b8" : "#166534",
                      border: "1px solid " + (isOff ? "#e2e8f0" : "#86efac"),
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                    data-testid={`cc-toggle-${d.idx}`}
                  >
                    {d.label}
                  </button>
                  {isOff ? (
                    <span style={{ color: "#94a3b8", fontSize: 13 }}>Day off</span>
                  ) : (
                    intervals.map((iv, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Input
                          type="time"
                          value={iv.start}
                          onChange={(e) => setInterval(d.idx, i, "start", e.target.value)}
                          style={{ width: 100 }}
                          data-testid={`cc-start-${d.idx}-${i}`}
                        />
                        <span style={{ fontSize: 12, color: "#64748b" }}>to</span>
                        <Input
                          type="time"
                          value={iv.end}
                          onChange={(e) => setInterval(d.idx, i, "end", e.target.value)}
                          style={{ width: 100 }}
                          data-testid={`cc-end-${d.idx}-${i}`}
                        />
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Blackout dates */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 8 }}>
            Blackout dates <span style={{ fontWeight: 400, color: "#64748b" }}>(vacations, holidays)</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Input
              type="date"
              onChange={(e) => { addBlackout(e.target.value); e.target.value = ""; }}
              data-testid="cc-blackout-add"
              style={{ width: 160 }}
            />
            {(cal.blackoutDates || []).map((d) => (
              <span key={d} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "#fef2f2", color: "#b91c1c", borderRadius: 14, fontSize: 12 }}>
                {d}
                <button onClick={() => removeBlackout(d)} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit" }}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Save */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
          {saved && (
            <span style={{ fontSize: 12, color: "#16a34a", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Check className="w-3 h-3" /> Saved & synced to WhatsApp
            </span>
          )}
          <Button onClick={save} disabled={saving} data-testid="cc-save-btn">
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Saving…</> : <><Save className="w-3.5 h-3.5 mr-1" /> Save calendar</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
